import { useEffect, useRef } from 'react';
import { documentAnchorSearch } from '../links/consoleLinkLanding';

/** Match opaque ids without interpolating them into CSS selectors. */
function byAttribute(root: HTMLElement, attribute: string, value: string | null): HTMLElement | undefined {
  if (!value) return undefined;
  return Array.from(root.querySelectorAll<HTMLElement>(`[${attribute}]`)).find((element) => element.getAttribute(attribute) === value);
}

/** The editor and comment threads arrive after room hydration, not route mount. */
export function followDocumentAnchor(root: HTMLElement, search: string): () => void {
  // A comment citation (`?comment=`) opens at its comment, like `commentId`.
  const params = new URLSearchParams(documentAnchorSearch(search));
  const blockId = params.get('blockId');
  const threadId = params.get('threadId');
  const commentId = params.get('commentId');
  if (!blockId && !threadId && !commentId) return () => {};
  let complete = false;
  let openingComments = false;
  let showingResolved = false;
  const observer = new MutationObserver(focus);
  function focus() {
    if (complete) return;
    if (threadId || commentId) {
      const toggle = root.querySelector<HTMLElement>('[data-testid="comments-toggle"]');
      if (toggle && !openingComments) { openingComments = true; toggle.click(); }
    }
    const thread = byAttribute(root, 'data-thread-id', threadId);
    const target = commentId ? byAttribute(root, 'data-comment-id', commentId)
      : threadId ? thread : byAttribute(root, 'data-decision-id', blockId);
    if (target?.classList.contains('decision-container') && !target.querySelector('.decision-root')) return;
    if (!target) {
      const filter = root.querySelector<HTMLElement>('[data-testid="comments-panel-resolved-filter"][aria-pressed="false"]');
      if ((threadId || commentId) && filter && !showingResolved) { showingResolved = true; filter.click(); }
      return;
    }
    // The editor mounts DOM before its hydration/render barrier reveals it.
    // Focusing a node inside that hidden subtree silently fails in browsers.
    if (target.closest('[hidden], [inert]')) return;
    complete = true;
    observer.disconnect();
    thread?.click();
    if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
    target.scrollIntoView?.({ block: 'center' });
    target.focus({ preventScroll: true });
  }
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-decision-id', 'data-thread-id', 'data-comment-id', 'hidden', 'inert'] });
  focus();
  return () => { complete = true; observer.disconnect(); };
}

export function DocumentLocationAnchor({ search }: { search: string }) {
  const marker = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const root = marker.current?.closest<HTMLElement>('.document-workspace');
    if (root) return followDocumentAnchor(root, search);
  }, [search]);
  return <span ref={marker} className="document-location-anchor" hidden />;
}
